export default {
	ids: ['rows', 'gap', 'walls', 'start', 'parallax', 'fill', 'light', 'light_angle', 'fog', 'rings', 'ring_speed'],
	glsl: `
// Focal length in su: fog's distances count from this far before the rows' centre.
const float ROW_FOCAL = 400.0;
// Enclosed rows' zoom at enclose 1, so the loops and their waves fit the frame.
const float LOOP_ZOOM = 0.5;
// Rows' pitch floor, su: a zero pitch still steps the rows (pipeline/index.js speed's rowsMin).
const float PITCH_MIN = 1.0;
// Count's row window in places from the centre (place = row − speed's travel) and a stretch of it no row shows: the runtime's (sea.js).
uniform vec2 uRowWin;
uniform vec2 uRowHole;

// Rows (rows 1): each row a strip width wide about its crest line, the line raised straight up by the sea, no thickness. The strip turns about that line to face the camera: one normal for all, the view axis less its part along the rows, so rows across the view stand up under a low camera, lanes running away lie down, and every row lies flat at tilt 90. walls 1 stands them upright (facing back). The face never leans toward the camera, so the ray never passes edge-on through it and the crest stays up on screen; the ray meets the faces in index order. Premultiplied, front to back over the ground: each row covers what is behind it, fog tinting it toward the ground.
// A row's hit's change over one pixel, from the rays' origin step dR and direction step dD: hit = rel − dir·(rel·nrm)/aside, differentiated.
vec3 rowStep(vec3 dR, vec3 dD, vec3 dir3, vec3 rel, vec3 nrm, float aside) {
	float rn = dot(rel, nrm);
	return dR - (dD * rn + dir3 * dot(dR, nrm)) / aside + dir3 * (rn * dot(dD, nrm) / (aside * aside));
}

vec4 rows() {
	Ray ray = viewRay;
	float unit = ray.unit;
	float k = uPerspective / (200.0 * uStageUnit);

	// Rows recede across the bands along the surface's axis at angle and run along them, the plane's axes (surface()), so at any angle lying rows are the plane's bands; they face the camera at 270. Up is toward the camera at tilt 90.
	float a = radians(uAngle);
	vec3 back = vec3(cos(a), sin(a), 0.0);
	vec3 along = vec3(-sin(a), cos(a), 0.0);
	vec3 up = vec3(0.0, 0.0, 1.0);
	// The view axis (camera(), roll and wobble turn only the screen) in the plane across the rows: the face's lean th from upright, 90° where the camera looks along them level.
	float pitch0 = radians(uTilt - 90.0);
	vec3 view = vec3(0.0, sin(pitch0), -cos(pitch0));
	float vb = dot(view, back);
	float vu = dot(view, up);
	float th = abs(vb) + abs(vu) > 1e-6 ? atan(-(vb < 0.0 ? -1.0 : 1.0) * vu, abs(vb)) : radians(90.0);
	// Within 20° of yaw of running along the view the rows lie down, so a level camera never sees them drop flat at 0° or 180° alone.
	float level = abs(vb) / max(sqrt(max(1.0 - vu * vu, 0.0)), 1e-6);
	th = mix((abs(vu) > 1e-6 ? sign(th) : 1.0) * radians(90.0), th, smoothstep(0.0, sin(radians(20.0)), level));
	th *= 1.0 - clamp(uWalls, 0.0, 1.0);
	float cl = cos(th);
	float sl = sin(th);
	vec3 nrm = cl * back - sl * up;
	vec3 upr = cl * up + sl * back;
	// The sea lies in the face: a lying row's crest would lift it off its plane into a parallel one, so the waves shrink as it leans, to none flat.
	float lean = abs(cl);

	float pixel = length(vec2(dFdx(ray.o.x), dFdy(ray.o.x)));
	float reach = length(ray.d);
	vec3 dir3 = ray.d / reach;
	vec3 R0 = ray.o + vec3(uPan * uStageUnit, 0.0);
	float aside = dot(dir3, nrm);
	// Where the rays meet: the camera. Nothing behind it is seen; parallel rays have none.
	float tCam = k > 0.0 ? -0.999 * reach / k : -1e30;

	// A pixel's footprint in the planes, up and along: a step across the ray, carried along it onto the plane. Up sums the two steps as fwidth does, so lying flat at tilt 90 a row's edge is the plane band's.
	vec3 e1 = normalize(vec3(1.0, 0.0, 0.0) - dir3 * dir3.x);
	vec3 e2 = cross(dir3, e1);
	vec3 p1 = e1 - dir3 * dot(e1, nrm) / aside;
	vec3 p2 = e2 - dir3 * dot(e2, nrm) / aside;
	float footUp = abs(dot(p1, upr)) + abs(dot(p2, upr));
	float footAlong = length(vec2(dot(p1, along), dot(p2, along)));
	// The rays' own change per pixel, for a row's exact hit step (rowStep): their origins move too (at tilt 90 each ray starts on the plane), which the footprint above ignores off the axis.
	vec3 dR1 = dFdx(R0);
	vec3 dR2 = dFdy(R0);
	vec3 dD1 = dFdx(dir3);
	vec3 dD2 = dFdy(dir3);

	// The pitch, centre to centre, is spacing + gap · width (gap 0 on ribbons, 1 the plane's); a ribbon is never wider than it. Grow scales each row's width by its place, the pitch held (growRate, lattice).
	float pitchSu = max(uSpacing + uGap * uWidth, PITCH_MIN);
	float pitch = pitchSu * unit;
	float fullHw = 0.5 * min(uWidth, pitchSu) * unit;
	float gm = growRate() * pitchSu;
	float hwMax = gm == 0.0 ? fullHw : 0.5 * pitch;
	// Count's window, its front cut on a row's edge where rows would come nearer than half the camera's distance along them (none looking straight down): past it, a row would cross behind the camera.
	float cut = ceil(-0.5 / max(k * abs(vb), 1e-9) / pitch - 1.0) + 0.5;
	vec2 win = max(uRowWin, vec2(cut));

	// The crest's bound: three trains peak at 1.5 × each wave's height.
	float crestMax = lean * 1.5 * (abs(uWaveHeight) + abs(uWaveBHeight) + abs(uWaveCHeight)) * unit;
	float du = dot(dir3, upr);
	// Lying, a crest lift H moves the strip's plane along up: the ray meets it ct·H farther on, cu·H further along the row, and the hit's distance across the strip changes by g·H (−1 upright).
	float ct = -sl / aside;
	float cu = ct * dot(dir3, along);
	float g = ct * du - cl;

	float lo = floor(uSpeed + win.x) - 1.0;
	float hi = floor(uSpeed + win.y + 0.5);
	// Upright to float precision (tilt 0 across the view, walls 1) skips the lying solve.
	bool leans = abs(sl) > 1e-6;
	// Filled, a row covers everything below its crest, so only the rows the crossing passes above are culled.
	if (uEnclose <= 0.0 && abs(aside) >= 1e-4) {
		// A row's height at the crossing is linear in its offset: keep the rows whose crossing lies near enough to show.
		float h0 = dot(R0, upr) - du * dot(R0, nrm) / aside;
		float slope = du * cl / aside - sl;
		float t0 = -dot(R0, nrm) / aside;
		float reachUp = hwMax + abs(g) * crestMax + 2.0 * pixel * max(1.0 + k * t0 / reach, 1.0) * footUp;
		if (abs(slope) > 1e-6) {
			float o1 = uFill > 0.0 ? (slope > 0.0 ? -1e30 : 1e30) : (-reachUp - h0) / slope;
			float o2 = (reachUp - h0) / slope;
			lo = max(lo, floor(min(o1, o2) / pitch + uSpeed) - 1.0);
			hi = min(hi, ceil(max(o1, o2) / pitch + uSpeed) + 1.0);
		}
	}
	float count = abs(aside) < 1e-4 ? 0.0 : hi - lo + 1.0;
	// Enclosed, a row's across = mix(hgt, inset − s·ring(q), enclose) and only its hit distance t moves with its offset, linearly: hgt, s and F = s·q are linear in t. Per su of offset the inset grows 1, hgt by its slope, s·ring by τ·(κ·(ring − ∇ring·q) + ∇ring·D): ring is 1-Lipschitz and 1-homogeneous in q but for constants within the frame's half-extent M, so ring − ∇ring·q lies in [0, M]. Rising at least 0.1 per su, one row's across brackets every row's, and only the rows within reach of the crest and the pixel step are walked.
	if (uEnclose > 0.0 && count > 0.0) {
		float tau = cl / aside;
		float t0 = -dot(R0, nrm) / aside;
		float kap = k / reach;
		vec2 ext = 0.5 * uResolution;
		float side = max(min(ext.x, ext.y), 1.0);
		vec2 D = dot(dir3, along) * along.xy + du * back.xy;
		vec2 F0 = dot(R0, along) * along.xy + dot(R0, upr) * back.xy;
		float slopeH = du * tau - sl;
		float ringRate = tau * kap * max(ext.x, ext.y);
		float bendRate = abs(tau) * length(D);
		float rateLo = mix(slopeH, 1.0 - max(ringRate, 0.0) - bendRate, uEnclose);
		float rateHi = mix(slopeH, 1.0 - min(ringRate, 0.0) + bendRate, uEnclose);
		float ta = t0 + tau * pitch * (lo - uSpeed - 1.0);
		float tb = t0 + tau * pitch * (hi - uSpeed);
		if (k > 0.0) {
			ta = max(ta, tCam);
			tb = max(tb, tCam);
		}
		float sMin = min(1.0 + kap * ta, 1.0 + kap * tb);
		float kr = floor(0.5 * (lo + hi));
		float offR = pitch * (kr - uSpeed);
		float tr = t0 + tau * offR;
		float sr = 1.0 + kap * tr;
		if (rateLo >= 0.1 && sMin > 1e-3 && tr >= tCam) {
			float hgtR = dot(R0, upr) + du * tr - sl * offR;
			float gr = mix(hgtR, offR - pitch * (win.x + 0.5) - sr * bentRing((F0 + D * tr) / sr, vec2(0.0), 0.0, 0.0, ext, side), uEnclose);
			// rowStep is linear in t: P + t·Q per screen axis, so its bound over the rows is at an end.
			vec3 P1 = dR1 - dir3 * dot(dR1, nrm) / aside;
			vec3 Q1 = dD1 - dir3 * dot(dD1, nrm) / aside;
			vec3 P2 = dR2 - dir3 * dot(dR2, nrm) / aside;
			vec3 Q2 = dD2 - dir3 * dot(dD2, nrm) / aside;
			float pxMax = max(length(P1 + Q1 * ta) + length(P2 + Q2 * ta), length(P1 + Q1 * tb) + length(P2 + Q2 * tb));
			float reachR = cl * crestMax + hwMax + 1.5 * pxMax;
			float below = -reachR - gr;
			float above = reachR - gr;
			// Filled, a row covers what is below its crest: only the rows past the pixel go.
			float dMin = uFill > 0.0 ? -1e30 : below / (below >= 0.0 ? rateHi : rateLo);
			float dMax = above / (above >= 0.0 ? rateLo : rateHi);
			// Three rows' margin: the entering row's offset trails its place by up to one.
			lo = max(lo, floor(kr + dMin / pitch) - 3.0);
			hi = min(hi, ceil(kr + dMax / pitch) + 3.0);
			count = max(hi - lo + 1.0, 0.0);
		}
	}
	bool ascending = aside >= 0.0;

	// Sea cells per stage unit.
	float kc = 1.0 / (SEA_CELL * unit);

	vec3 acc = vec3(0.0);
	float clear = 1.0;
	for (int i = 0; i < MAX_ROWS; i++) {
		if (float(i) >= count || clear < 0.002) break;
		float kk = ascending ? lo + float(i) : hi - float(i);
		float place = kk - uSpeed;
		// x: rows behind the front one.
		float x = place - win.x - 0.5;
		// A row's share of the window less the hole's: it fades in and out at the edges.
		float share = max(countShare(place, win) - countShare(place, uRowHole), 0.0);
		if (share <= 0.0) continue;
		// The slice's place across the rows from the central one. The row entering at the back grows out of the one in front: width and gap from 0 with its entry e, its centre ½·width + e·(pitch − ½·width) behind that row's.
		float w = place;
		float rowHw = gm == 0.0 ? fullHw : min(fullHw * exp(-clamp(gm * place, -GROW_CLAMP, GROW_CLAMP)), hwMax);
		float hw = rowHw;
		float entering = clamp(win.y - (place - 0.5), 0.0, 1.0);
		if (entering < 1.0) {
			hw = rowHw * entering;
			float edge = min(rowHw, pitch);
			w = place - 1.0 + (edge + entering * (pitch - edge)) / pitch;
		}
		// The row's number, travelling with it: its parallax offset's.
		float row = kk + uSpeedTurns;
		// Sine waves at the row's own place read its closed form (sea.js).
		bool closed = uShape <= 0.0 && entering >= 1.0;
		float off = pitch * w;
		vec3 rel = R0 - back * off;
		float t = -dot(rel, nrm) / aside;
		float s = 1.0 + k * t / reach;
		vec3 hit = rel + dir3 * t;
		float u = dot(hit, along);
		float hgt = dot(hit, upr);
		float px = pixel * s * footUp;
		float shows = max(hw, 0.5 * px) + px;

		// The view's point in the sea, in cells: along the row, and across the rows hill_rows a row whatever the pitch.
		float fine = kc * pixel * s * footAlong;
		if (leans && uEnclose <= 0.0) {
			// Lying: the lift H where the ray meets the strip solves H = crest(u + cu·H); two secant steps from H 0, then the hit moves there.
			if ((uFill <= 0.0 ? abs(hgt) : hgt) - abs(g) * crestMax > shows) continue;
			float c0 = lean * (closed ? rowCrestAlong(kc * u, row, fine, unit) : rowCrestAt(vec2(kc * u, w * uHillRows), row, fine, unit));
			float f1 = lean * (closed ? rowCrestAlong(kc * (u + cu * c0), row, fine, unit) : rowCrestAt(vec2(kc * (u + cu * c0), w * uHillRows), row, fine, unit)) - c0;
			float lift = abs(f1 - c0) > 1e-6 ? c0 - f1 * c0 / (f1 - c0) : c0;
			lift = clamp(lift, -crestMax, crestMax);
			t += ct * lift;
			s = 1.0 + k * t / reach;
			hit = rel + dir3 * t;
			u = dot(hit, along);
			hgt = dot(hit, upr);
			px = pixel * s * footUp;
			shows = max(hw, 0.5 * px) + px;
		}
		if (t < tCam) continue;
		vec3 h1 = rowStep(dR1, dD1, dir3, rel, nrm, aside);
		vec3 h2 = rowStep(dR2, dD2, dir3, rel, nrm, aside);
		px = abs(dot(h1, upr)) + abs(dot(h2, upr));
		shows = max(hw, 0.5 * px) + px;
		// Start: the rows begin start su ahead of the eye (where the rays meet, the same point for every pixel) along the ground's forward axis (−y: tilt pitches about x), so a low camera sees the field from its edge rather than from inside it; in camera units, not zoom's. Parallel rays (perspective 0) have no eye: inert. The end smoothed over its pixel step.
		if (uStart > 0.0 && k > 0.0) {
			float eyeY = R0.y - ray.d.y / k;
			share *= clamp((eyeY - hit.y - uStart * uStageUnit) / max(abs(h1.y) + abs(h2.y), 1e-6) + 0.5, 0.0, 1.0);
			if (share <= 0.0) continue;
		}
		// Enclose: the row bends round a loop in its face, the level set x pitches in of the plane's enclosed ring coordinate (bentRing at enclose 1, fit and rounding included) at the row's scale, so the rows nest inward from the front one on the outline, as the painted rings, and lying flat at tilt 90 each loop is a painted ring. The face maps onto the frame by the plane's axes (along, back), exact for a lying row. across: out from the loop; its pixel footprint is the ring coordinate's change over one pixel step each way, as fwidth. The sea is read round a circle of the inset frame's perimeter, so it closes smooth with no seam. Lying, no lift and no row window.
		float across = hgt;
		vec2 f = vec2(kc * u, w * uHillRows);
		if (uEnclose > 0.0) {
			float inset = pitch * x;
			vec2 hk = max(0.5 * s * uResolution - inset, pixel);
			vec2 face = vec2(u, hgt + sl * off);
			vec2 q = (face.x * along.xy + face.y * back.xy) / s;
			vec2 ext = 0.5 * uResolution;
			float side = max(min(ext.x, ext.y), 1.0);
			float ring = bentRing(q, vec2(0.0), 0.0, 0.0, ext, side);
			vec2 step1 = (dot(h1, along) * along.xy + dot(h1, upr) * back.xy) / s;
			vec2 step2 = (dot(h2, along) * along.xy + dot(h2, upr) * back.xy) / s;
			// bentRing is 1-Lipschitz, so its pixel step is at most the steps' lengths: a row out of reach by that bound skips the two steps' rings and the crest.
			float pxBound = mix(px, s * (length(step1) + length(step2)), uEnclose);
			across = mix(hgt, inset - s * ring, uEnclose);
			if ((uFill <= 0.0 ? abs(across) : across) - cl * crestMax > max(hw, 0.5 * pxBound) + pxBound) continue;
			float pxRing = s * (abs(bentRing(q + step1, vec2(0.0), 0.0, 0.0, ext, side) - ring) + abs(bentRing(q + step2, vec2(0.0), 0.0, 0.0, ext, side) - ring));
			px = mix(px, pxRing, uEnclose);
			shows = max(hw, 0.5 * px) + px;
			vec2 e = face / hk;
			float r = max(length(e), 1e-6);
			// Ramanujan's perimeter over TAU: the circle's radius, in sea cells.
			float rad = kc * 0.5 * (3.0 * (hk.x + hk.y) - sqrt((3.0 * hk.x + hk.y) * (hk.x + 3.0 * hk.y)));
			f = mix(f, vec2(0.0, w * uHillRows) + e * (rad / r), uEnclose);
		}
		// Out of reach of the highest crest (filled: above it): skip the sea.
		if ((uFill <= 0.0 ? abs(across) : across) - cl * crestMax > shows) continue;
		float crest = lean * (closed && uEnclose <= 0.0 ? rowCrestAlong(f.x, row, fine, unit) : rowCrestAt(f, row, fine, unit));
		if (uFill <= 0.0 ? abs(across) - cl * abs(crest) > shows : across - cl * crest > shows) continue;
		// Across the strip from its crest line, in its face. Fill: below that line a row reaches uFill × twice the frame's height in pixels at its depth, so at 1 it is opaque to the frame's bottom.
		float below = cl * crest - across;
		float d = below > 0.0 ? max(below - uFill * 2.0 * uResolution.y * px, 0.0) : -below;

		// Under a pixel wide, a row dims to its share of the pixel rather than breaking up; its edge smoothed over a pixel either side as the plane's.
		float hwE = max(hw, 0.5 * px);
		float cover = share * (1.0 - smoothstep(hwE - px, hwE + px, d)) * hw / hwE;
		if (cover < 0.002) continue;
		// Row kk is band kk's entry, open or round the outline: the painted band or ring it lies on, fog tinting it toward the ground.
		float fogged = exp(-uFog * max(t / unit + 0.2 * ROW_FOCAL, 0.0) / 50.0);
		vec3 tint = mix(uGround.rgb, bandColor(kk, uSpeedTurns, 0.0), fogged);
		acc += clear * tint * cover;
		clear *= 1.0 - cover;
	}
	vec4 col = vec4(acc, 1.0 - clear);
	return col + groundColor() * (1.0 - col.a);
}

// The light's lean toward the viewer, against its unit part round the tunnel; the shade of the face turned furthest from it; fog's distance scale, su.
const float LIGHT_LEAN = 0.5;
const float LIGHT_AMBIENT = 0.3;
const float FOG_SU = 400.0;

// The plane and the tube (rows 0), premultiplied. Rings: accent bands of the ground's colour down the tube, one a radius, travelling at ring_speed; light: one direction on the surface normal (y down the screen), brightest on the wall at light_angle round the tunnel (0 the top, 90 the right), wrapped so each face's shade follows its angle to the light all the way round, light 0 flat; fog: one rule for plane and tube, the hit's distance from the screen plane fading to the ground. Each at 0 is the identity and compiles out locked.
vec4 plane(vec4 col, Surf s) {
	if (uRings > 0.0 && s.cube == 0.0) {
		float f = abs(fract(s.axial / TUBE_RADIUS - uRingSpeed) - 0.5);
		float aa = s.dDepth / TUBE_RADIUS;
		float ring = uRings * (1.0 - smoothstep(0.05 - aa, 0.05 + aa, f)) * (1.0 - smoothstep(0.1, 0.4, aa));
		col = mix(col, vec4(uGround.rgb, 1.0), ring);
	}
	if (uLight > 0.0) {
		float a = radians(uLightAngle);
		float lit = mix(LIGHT_AMBIENT, 1.0, 0.5 + 0.5 * dot(s.n, normalize(vec3(-sin(a), cos(a), -LIGHT_LEAN))));
		col.rgb *= mix(1.0, lit, uLight);
	}
	if (uFog > 0.0) col = mix(groundColor(), col, exp(-uFog * s.depth / FOG_SU));
	return col;
}

vec4 light(vec4 col, Surf s, Cell c) {
	if (uRows > 0.0) {
		vec4 r = rows();
		if (uCross <= 0.0) return r;
		// A path from a crossed plane: its picture on the cross family's own lines (cov.y carries cross), so at the plane end only the renderer changes.
		return mix(r, plane(col, s), c.cov.y);
	}
	return plane(col, s);
}
`,
	// The pick build splices each glsl after its `at` (`src/lib/drag/pick.js`): the claiming row writes `pat`, its line the row index less its lift in row steps.
	pick: [
		{ at: 'Ray ray = viewRay;\n', glsl: '\tpat.hit = 0.0;\n' },
		{
			at: 'float crest = lean * (closed && uEnclose <= 0.0 ? rowCrestAlong(f.x, row, fine, unit) : rowCrestAt(f, row, fine, unit));\n',
			glsl: `		if (pat.hit == 0.0) {
			float rise = (dot(dir3, upr) * cl / aside - sl) * pitch;
			float lift = (hgt - crest) / max(abs(rise), 2.0 * hw) * (rise < 0.0 ? -1.0 : 1.0);
			if (abs(lift) <= 0.5) {
				pat.line = kk - lift;
				pat.along = u / unit;
				pat.phase = 0.0;
				pat.hit = 1.0;
			}
		}
`,
		},
	],
}
